import { stripEmbeddedEmailEditorDesign } from '@/helpers';
import { getImageFromHtml, getTextFromHtml } from '@/helpers/messageFormatters';
import { mapMessageRecipientToValue } from '@/helpers/messageRecipients';
import dayjs from '@/utils/dayjs';
import { MESSAGE_REPLY_OPTION, type Message } from '@/types';

export const MAX_SENDER_NAME_LENGTH = 100;

export const hasMessageContent = ({ value }: { value: string }): boolean => {
  const hasTextContent = getTextFromHtml(value).trim().length > 0;
  if (hasTextContent) {
    return true;
  }
  return Boolean(getImageFromHtml(value));
};

export type MessageCreatorFormData = {
  messageTitle: string;
  messageText: string;
  formAttachments: File[];
  senderName: string;
  recipientsValue: string[];
  directMessageAdultConfirmed: boolean;
  allowReplies: boolean;
  checkboxValues: string[];
  additionalEmails: string[];
  announcementStartDate: Date | null;
  announcementStartTime: string;
  announcementEndDate: Date | null;
  announcementEndTime: string;
  previewTitle: string;
  previewText: string;
  isFacebookEnabled: boolean;
  facebookPageIds: string[];
  isSMSEnabled: boolean;
  // TODO: Twitter/X is temporarily disabled. This code may be deleted later.
  // isXEnabled: boolean;
  isWebsiteEnabled: boolean;
  isAnnouncementEnabled: boolean;
  actionText: string;
};

const parseUTCDateToLocal = (utcDateString: string | null, timezone?: string): { date: Date | null; time: string } => {
  if (!utcDateString || !timezone) {
    return { date: null, time: '' };
  }

  const utcDate = dayjs.utc(utcDateString);
  const localDate = utcDate.tz(timezone);
  return {
    date: localDate.toDate(),
    time: localDate.format('h:mm'),
  };
};

export const convertMessageToFormData = (
  message: Message,
  actionText: string,
  timezone?: string
): MessageCreatorFormData => {
  const recipientsValue: string[] = (message.recipients || [])
    .map((recipient) => mapMessageRecipientToValue({ recipient }))
    .filter(Boolean);

  const replySettings = message.replySettings || {
    isAllow: false,
    isToAdminsOnly: false,
    isToGroupAdminsOnly: false,
    isToAllMembers: false,
    additionalEmails: [],
  };

  const checkboxValues: string[] = [];
  if (replySettings.isToAdminsOnly) {
    checkboxValues.push(MESSAGE_REPLY_OPTION.IS_TO_ADMINS_ONLY);
  }
  if (replySettings.isToGroupAdminsOnly) {
    checkboxValues.push(MESSAGE_REPLY_OPTION.IS_TO_GROUP_ADMINS_ONLY);
  }
  if (replySettings.isToAllMembers) {
    checkboxValues.push(MESSAGE_REPLY_OPTION.IS_TO_ALL_MEMBERS);
  }
  if (replySettings.additionalEmails && replySettings.additionalEmails.length > 0) {
    checkboxValues.push(MESSAGE_REPLY_OPTION.ADDITIONAL_EMAILS);
  }

  const announcement = message.announcement || {
    subject: '',
    content: '',
    isEnabled: false,
    startDate: null,
    endDate: null,
  };

  const announcementStart = parseUTCDateToLocal(announcement.startDate, timezone);
  const announcementEnd = parseUTCDateToLocal(announcement.endDate, timezone);

  const facebook = message.facebook || { isSend: false, content: null, pageIds: [] };
  const sms = message.sms || { isSend: false, content: null };
  // TODO: Twitter/X is temporarily disabled. This code may be deleted later.
  // const twitter = message.twitter || { isSend: false, content: null };
  const web = message.web || { isSend: false, content: null };

  return {
    messageTitle: message.title || '',
    messageText: stripEmbeddedEmailEditorDesign({ html: message.content || '' }),
    formAttachments: [],
    senderName: (message.senderName || '').slice(0, MAX_SENDER_NAME_LENGTH),
    recipientsValue: Array.isArray(recipientsValue) ? recipientsValue : [],
    directMessageAdultConfirmed: false,
    allowReplies:
      replySettings.isAllow ||
      replySettings.isToAdminsOnly ||
      replySettings.isToGroupAdminsOnly ||
      replySettings.isToAllMembers ||
      (replySettings.additionalEmails &&
        Array.isArray(replySettings.additionalEmails) &&
        replySettings.additionalEmails.length > 0),
    checkboxValues: Array.isArray(checkboxValues) ? checkboxValues : [],
    additionalEmails: Array.isArray(replySettings.additionalEmails) ? replySettings.additionalEmails : [],
    announcementStartDate: announcementStart.date,
    announcementStartTime: announcementStart.time,
    announcementEndDate: announcementEnd.date,
    announcementEndTime: announcementEnd.time,
    previewTitle: announcement.subject || '',
    previewText: announcement.content || '',
    isFacebookEnabled: facebook.isSend || false,
    facebookPageIds: Array.isArray(facebook.pageIds) ? facebook.pageIds : [],
    isSMSEnabled: sms.isSend || false,
    // TODO: Twitter/X is temporarily disabled. This code may be deleted later.
    // isXEnabled: twitter.isSend || false,
    isWebsiteEnabled: web.isSend || false,
    isAnnouncementEnabled: announcement.isEnabled || false,
    actionText,
  };
};

/**
 * Formats a scheduled date for display
 * @param scheduleDate - The scheduled date
 * @param timezone - The timezone string (e.g., 'America/New_York')
 * @returns Formatted string describing when the message will be sent
 */
export const formatScheduledDate = ({
  scheduleDate,
  timezone,
}: {
  scheduleDate: Date | null;
  timezone?: string;
}): string => {
  if (!scheduleDate || !timezone) {
    return '';
  }

  const localDate = dayjs.tz(dayjs(scheduleDate).format('YYYY-MM-DD'), 'YYYY-MM-DD', timezone);

  return `This message will be sent on ${localDate.format('MMM D, YYYY')} ${timezone}`;
};

const MESSAGE_CREATOR_FORM_FIELD_ORDER: (keyof MessageCreatorFormData)[] = [
  'messageTitle',
  'messageText',
  'senderName',
  'recipientsValue',
  'checkboxValues',
  'additionalEmails',
  'previewTitle',
  'previewText',
  'facebookPageIds',
];

export const wrapContentInHtml = (content: string | null): string | null => {
  if (!content) return null;
  let html = stripEmbeddedEmailEditorDesign({ html: content.trim() });

  const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  if (bodyMatch) {
    html = bodyMatch[1];
  } else {
    html = html.replace(/<!DOCTYPE[^>]*>/i, '');
    html = html.replace(/<head[^>]*>[\s\S]*?<\/head>/gi, '');
    html = html.replace(/<\/?html[^>]*>/gi, '');
    html = html.replace(/^<body[^>]*>([\s\S]*?)<\/body>$/i, '$1');
  }

  html = html.replace(/<!--[\s\S]*?-->/g, '');
  html = html.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');
  html = html.replace(/\son\w+="[^"]*"/gi, '');
  html = html.replace(/style=(['"])([\s\S]*?)\1/gi, (_match, quote, style) => {
    const cleaned = String(style)
      .replace(/(^|;)\s*mso-[^:]+:[^;]+;?/gi, ';')
      .replace(/;;+/g, ';')
      .replace(/^;|;$/g, '');
    return `style=${quote}${cleaned}${quote}`;
  });
  html = html.replace(/\sclass="mso-[^"]*"/gi, '');
  html = html.replace(/\sxmlns(:\w+)?="[^"]*"/gi, '');
  html = html.replace(/>\s*\n\s*</g, '><');
  html = html.replace(/\n{2,}/g, '\n').trim();

  return html;
};

export const scrollToFirstMessageCreatorFormError = ({
  errors,
}: {
  errors: Partial<Record<keyof MessageCreatorFormData, unknown>>;
}) => {
  const firstErrorField = MESSAGE_CREATOR_FORM_FIELD_ORDER.find((field) => Boolean(errors[field]));

  if (!firstErrorField) {
    return;
  }

  const element = document.querySelector(`[data-form-field="${firstErrorField}"]`);

  if (!element || typeof (element as HTMLElement).scrollIntoView !== 'function') {
    return;
  }

  (element as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
};
